import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, IconButton } from '@mui/material';
import { useCallback } from 'react';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getObjectByPath } from '../../utils/object-path-utils';

export interface IColumn<RowData extends Record<string, any>> {
	field?: keyof RowData | string;
	title?: string | React.ReactElement<any>;
	render?: (data: RowData, type: 'row' | 'group') => any;
}

export enum TableType {
	CONVICTIONS = 'CONVICTIONS',
	DRIVERS = 'DRIVERS',
	CLAIMS = 'CLAIMS',
	MODIFICATIONS = 'MODIFICATIONS',
}

interface IProps<RowData extends Record<string, any>> {
	data: RowData[];
	columns: IColumn<RowData>[];
	onEdit: (rowDate: RowData, index: number) => void;
	onRemove: (rowDate: RowData, index: number) => void;
	isEditing?: boolean;
	setMainDriver?: (rowDate: RowData, index: number) => void;
	tableType: TableType;
}
const MiniTable = <RowDate extends Record<string, any>>({
	data,
	columns,
	onEdit,
	onRemove,
	isEditing,
	setMainDriver,
	tableType,
}: IProps<RowDate>) => {
	let DISABLED_REMOVE_TT = '';
	let REGULAR_REMOVE_TT = '';

	if (tableType === TableType.DRIVERS) {
		DISABLED_REMOVE_TT = 'Cannot remove a driver while editing.';
		REGULAR_REMOVE_TT = 'Remove driver';
	} else if (tableType === TableType.CLAIMS) {
		DISABLED_REMOVE_TT = 'Cannot remove a claim while editing.';
		REGULAR_REMOVE_TT = 'Remove claim';
	} else if (tableType === TableType.CONVICTIONS) {
		DISABLED_REMOVE_TT = 'Cannot remove a conviction while editing.';
		REGULAR_REMOVE_TT = 'Remove conviction';
	} else if (tableType === TableType.MODIFICATIONS) {
		DISABLED_REMOVE_TT = 'Cannot remove a modification while editing.';
		REGULAR_REMOVE_TT = 'Remove modification';
	}

	const removeTxt = useCallback(() => {
		if (isEditing) {
			return DISABLED_REMOVE_TT;
		}
		return REGULAR_REMOVE_TT;
	}, [isEditing, DISABLED_REMOVE_TT, REGULAR_REMOVE_TT]);

	return (
		<TableContainer
			sx={(theme) => ({
				width: '100%',
				border: `1px solid ${theme.palette.grey[300]}`,
				borderRadius: '8px',
				overflowX: 'hidden',
			})}
		>
			<Table aria-label='simple table' sx={{ tableLayout: 'fixed' }}>
				<TableHead
					sx={(theme) => ({
						minWidth: '350px',
						'& th': {
							padding: '16px 0.5rem',
							borderRight: `1px solid ${theme.palette.grey[300]}`,
							'&:last-child': {
								borderRight: 0,
							},
						},
						borderBottom: `1x solid ${theme.palette.grey[300]}`,
					})}
				>
					<TableRow>
						{columns.map((column) => (
							<TableCell key={column.field?.toString()}>{column.title}</TableCell>
						))}
						<TableCell key='th-options' sx={{ textAlign: 'center' }}>
							Options
						</TableCell>
						{setMainDriver && (
							<TableCell key='th-main-driver' sx={{ textAlign: 'center' }}>
								Main Driver
							</TableCell>
						)}
					</TableRow>
				</TableHead>
				<TableBody>
					{data.map((rowData, index) => (
						<TableRow
							key={`table-body-row+${data[index].licenceNumber}`}
							sx={(theme) => ({
								'& th,td': {
									padding: '0.5rem',
									borderRight: `1px solid ${theme.palette.grey[300]}`,
									overflowWrap: 'break-word',
								},
							})}
						>
							{columns.map((columnDef) => {
								if (columnDef.render) {
									return (
										<TableCell key={`table-body-cell+${columnDef.field as string}`}>
											{columnDef.render(rowData, 'row')}
										</TableCell>
									);
								}
								return (
									<TableCell key={`table-body-cell+${columnDef.field as string}`}>
										{
											(getObjectByPath(rowData, columnDef.field as string),
											(columnDef.field as string).split('.')[1] || (columnDef.field as string))
										}
									</TableCell>
								);
							})}
							<TableCell
								sx={(theme) => ({
									borderRight: !setMainDriver ? 'unset !important' : `1px solid ${theme.palette.grey[300]}`,
									width: 'auto',

									whiteSpace: 'nowrap',
								})}
								align='center'
							>
								<Tooltip title='Edit'>
									<span>
										<IconButton color='primary' disabled={isEditing} onClick={() => onEdit(rowData, index)}>
											<EditIcon />
										</IconButton>
									</span>
								</Tooltip>
								{setMainDriver && index === 0 ? null : (
									<Tooltip title={removeTxt()}>
										<span>
											<IconButton color='primary' disabled={isEditing} onClick={() => onRemove(rowData, index)}>
												<DeleteIcon />
											</IconButton>
										</span>
									</Tooltip>
								)}
							</TableCell>
							{setMainDriver && (
								<TableCell
									sx={{
										borderRight: 'unset !important',
										width: 'auto',

										whiteSpace: 'nowrap',
									}}
									align='center'
								>
									<Tooltip title='Set Main Driver'>
										<span>
											<IconButton onClick={() => setMainDriver(rowData, index)}>
												{rowData.isMainDriver ? (
													<CheckCircleIcon fontSize='large' color='primary' />
												) : (
													<CircleOutlinedIcon fontSize='large' />
												)}
											</IconButton>
										</span>
									</Tooltip>
								</TableCell>
							)}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default MiniTable;
